// Product details panel — shows brand, image, allergens, ingredients, nutrition, Nutri-Score, NOVA.
import { AlertTriangle, Leaf, Factory, HeartPulse } from "lucide-react";

const GRADE_COLOR = { A: "#0d8f4f", B: "#7fbc3a", C: "#f4b400", D: "#ef7c1a", E: "#e2382d" };
const NOVA_COLOR = { 1: "#0d8f4f", 2: "#7fbc3a", 3: "#f4b400", 4: "#e2382d" };
const NOVA_LABEL = { 1: "غير مصنع", 2: "مكونات مطبخية", 3: "معالج", 4: "معالج بشدة" };

const fmt = (n, unit = "g") => (n == null ? "—" : `${Number(n).toFixed(n < 10 ? 1 : 0)}${unit}`);

function Row({ k, v }) {
  return (
    <div className="flex justify-between py-1 text-[13px] border-b border-slate-200/60 last:border-b-0">
      <span className="text-slate-500">{k}</span>
      <span className="tabular-nums font-semibold">{v}</span>
    </div>
  );
}

function ScorePill({ Icon, title, subtitle, val, color }) {
  return (
    <div className="flex items-center gap-2.5 p-3 rounded-2xl neu-sm">
      <div className="w-10 h-10 rounded-xl grid place-items-center text-white shrink-0" style={{ background: color || "#6b7280" }}>
        <span className="font-extrabold text-lg">{val}</span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1 text-[11px] uppercase tracking-widest text-slate-500 font-bold"><Icon className="w-3 h-3" />{title}</div>
        <p className="text-[11px] text-slate-600 truncate">{subtitle}</p>
      </div>
    </div>
  );
}

export function ProductDetails({ p, code }) {
  if (!p) return null;
  const n = p.nutrition || {};
  const hasNut = n.kcal != null || n.fat != null || n.carbs != null || n.protein != null;
  const hasScores = p.nutriscore || p.nova || p.ecoscore;
  const hasExtras = hasScores || p.ingredients || p.allergens?.length || hasNut;
  const scoreDesc = { A: "ممتاز", B: "جيد", C: "متوسط", D: "ضعيف", E: "سيئ" };
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3 pe-10">
        {p.image && <img src={p.image} alt="" className="w-20 h-20 rounded-2xl object-cover neu-sm shrink-0" />}
        <div className="min-w-0 flex-1 space-y-0.5">
          <p className="font-display font-extrabold text-base neu-accent-blue leading-tight break-words">{p.name}</p>
          {p.brand && <p className="text-xs text-slate-600 truncate">{p.brand}</p>}
          <p className="text-[10px] text-slate-400 font-mono tabular-nums">{code}{p.quantity ? ` · ${p.quantity}` : ""}</p>
        </div>
      </div>
      {!hasExtras && (
        <div className="p-3 rounded-2xl bg-blue-50 border border-blue-200 text-xs text-blue-800 leading-relaxed">
          هذا المنتج معرّف بنجاح ✓ — التفاصيل الغذائية غير متوفرة، وهذا طبيعي للسجائر ومستحضرات التجميل والمنظفات والإلكترونيات وغيرها من المنتجات غير الغذائية.
        </div>
      )}
      {hasScores && (
        <div className="grid grid-cols-2 gap-2 [&>*:last-child:nth-child(odd)]:col-span-2">
          {p.nutriscore && <ScorePill Icon={HeartPulse} title="Nutri-Score" subtitle={`القيمة الغذائية · ${scoreDesc[p.nutriscore] || ""}`} val={p.nutriscore} color={GRADE_COLOR[p.nutriscore]} />}
          {p.nova && <ScorePill Icon={Factory} title="NOVA" subtitle={`مستوى المعالجة · ${NOVA_LABEL[p.nova] || ""}`} val={p.nova} color={NOVA_COLOR[p.nova]} />}
          {p.ecoscore && <ScorePill Icon={Leaf} title="Eco-Score" subtitle={`الأثر البيئي · ${scoreDesc[p.ecoscore] || ""}`} val={p.ecoscore} color={GRADE_COLOR[p.ecoscore]} />}
        </div>
      )}
      {p.allergens?.length > 0 && (
        <div className="p-3 rounded-2xl bg-red-50 border border-red-200 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
          <p className="text-xs text-red-800"><b>محسسات:</b> {p.allergens.join("، ")}</p>
        </div>
      )}
      {p.traces?.length > 0 && <p className="text-[11px] text-amber-800 px-1">قد يحتوي آثار: {p.traces.join("، ")}</p>}
      {p.ingredients && (
        <details className="rounded-2xl neu-sm p-3" open>
          <summary className="text-xs font-bold text-slate-700 cursor-pointer">المكونات</summary>
          <p className="text-[12px] text-slate-600 mt-2 leading-relaxed break-words">{p.ingredients}</p>
        </details>
      )}
      {hasNut && (
        <div className="rounded-2xl neu-sm p-3">
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">القيم الغذائية / 100غ</p>
          <Row k="السعرات" v={n.kcal != null ? `${Math.round(n.kcal)} kcal` : "—"} />
          <Row k="الدهون" v={fmt(n.fat)} />
          <Row k="الكربوهيدرات" v={fmt(n.carbs)} />
          <Row k="السكريات" v={fmt(n.sugars)} />
          <Row k="البروتين" v={fmt(n.protein)} />
          <Row k="الملح" v={fmt(n.salt)} />
        </div>
      )}
    </div>
  );
}
